import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const DigitalPhilosophy = () => {
  const [activePhilosophy, setActivePhilosophy] = useState(0);
  const [isTyping, setIsTyping] = useState(false);

  const philosophies = [
    {
      title: 'Building Beyond Code',
      subtitle: 'From Vision to Reality',
      icon: 'Terminal', 
      color: '#1e90ff', 
      manifesto: `For me, technology is more than syntax and scripts—it’s a way to turn ideas into living, breathing experiences. Every project is a chance to solve real problems, inspire creativity, and push past the boundaries of what's possible.\n\nI see every line of code as an opportunity to shape a better digital world—one solution, one innovation at a time.`,
      principles: [
        'Impact over Output',
        'Solutions over Shortcuts',
        'Creativity over Routine',
        'Vision over Limitation'
      ]
    },
    {
      title: 'Human-Driven Innovation',
      subtitle: 'Technology with Purpose',
      icon: 'HeartHandshake',
      color: '#00c896', 
      manifesto: `The best tech is the kind that makes people’s lives better. Whether I’m mentoring future developers or building tools for clients, my focus is on making technology intuitive, accessible, and meaningful.\n\nI believe innovation isn’t about replacing people—it’s about amplifying their potential and creating experiences that matter.`,
      principles: [
        'Empathy in Design',
        'Accessibility for All',
        'Purpose-Driven Development',
        'Human-First Approach'
      ]
    },
    {
      title: 'Relentless Growth',
      subtitle: 'Evolving with the Times',
      icon: 'TrendingUp',
      color: '#ff6f61',
      manifesto: `In tech, standing still means falling behind. I embrace change, chase new skills, and adapt to whatever the future brings. From backend systems to AI tools, I see every challenge as a step forward.\n\nGrowth is not a phase—it’s a permanent mindset.`,
      principles: [
        'Lifelong Learning',
        'Adaptive Thinking',
        'Experimentation',
        'Growth over Comfort'
      ]
    },
    {
      title: 'Stronger Together',
      subtitle: 'Mentorship and Collaboration',
      icon: 'Users', 
      color: '#ffb400',
      manifesto: `True success is shared. I’ve learned that my greatest impact comes from helping others grow—whether through mentorship, collaboration, or sharing knowledge freely.\n\nThe more we build each other up, the further we all go.`,
      principles: [
        'Mentorship Culture',
        'Collaboration over Competition',
        'Community Building',
        'Knowledge Sharing'
      ]
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActivePhilosophy((prev) => (prev + 1) % philosophies.length);
    }, 8000);

    return () => clearInterval(interval);
  }, []);

  const handlePhilosophyChange = (index) => {
    setActivePhilosophy(index);
    setIsTyping(true);
    setTimeout(() => setIsTyping(false), 100);
  };

  return (
    <div className="w-full max-w-6xl mx-auto">
      <motion.div
        className="text-center mb-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="font-orbitron font-bold text-3xl text-foreground mb-4">
          Digital Philosophy
        </h2>
        <p className="text-text-secondary font-inter max-w-2xl mx-auto">
          The manifesto on technological transcendence and the principles that guide the journey into the digital future
        </p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Philosophy Navigation */}
        <div className="lg:col-span-1">
          <div className="space-y-4">
            {philosophies.map((philosophy, index) => (
              <motion.div
                key={index}
                className={`glass-morphism rounded-xl p-4 cursor-pointer transition-all duration-300 ${
                  activePhilosophy === index 
                    ? 'cyber-border-active cyber-glow' :'cyber-border hover:cyber-glow'
                }`}
                onClick={() => handlePhilosophyChange(index)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center space-x-3">
                  <div
                    className={`w-12 h-12 rounded-lg flex items-center justify-center transition-all duration-300 ${
                      activePhilosophy === index ? 'cyber-glow-intense' : ''
                    }`}
                    style={{ 
                      backgroundColor: `${philosophy.color}20`, 
                      borderColor: philosophy.color,
                      border: activePhilosophy === index ? `2px solid ${philosophy.color}` : `1px solid ${philosophy.color}40`
                    }}
                  >
                    <Icon 
                      name={philosophy.icon} 
                      size={24} 
                      style={{ color: philosophy.color }} 
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className={`font-inter font-semibold transition-colors duration-300 ${
                      activePhilosophy === index ? 'text-foreground' : 'text-text-secondary'
                    }`}>
                      {philosophy.title}
                    </h3>
                    <p className="text-xs text-text-tertiary">
                      {philosophy.subtitle}
                    </p>
                  </div>
                </div>

                {/* Active Indicator */}
                {activePhilosophy === index && (
                  <motion.div
                    className="mt-3 h-1 rounded-full"
                    style={{ backgroundColor: philosophy.color }}
                    initial={{ width: 0 }}
                    animate={{ width: '100%' }}
                    transition={{ duration: 0.5 }}
                  />
                )}
              </motion.div>
            ))}
          </div>
        </div>

        {/* Philosophy Content */}
        <div className="lg:col-span-2">
          <AnimatePresence mode="wait">
            <motion.div
              key={activePhilosophy}
              className="glass-morphism rounded-2xl p-8 cyber-border-active cyber-glow h-full"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.5 }}
            >
              {(() => {
                const philosophy = philosophies[activePhilosophy];
                return (
                  <div className="h-full flex flex-col">
                    {/* Header */}
                    <div className="flex items-center space-x-4 mb-6">
                      <div
                        className="w-16 h-16 rounded-xl flex items-center justify-center cyber-glow-intense"
                        style={{ backgroundColor: `${philosophy.color}20`, borderColor: philosophy.color }}
                      >
                        <Icon name={philosophy.icon} size={32} style={{ color: philosophy.color }} />
                      </div>
                      <div>
                        <h3 className="font-orbitron font-bold text-2xl text-foreground">
                          {philosophy.title}
                        </h3>
                        <p className="font-mono text-sm" style={{ color: philosophy.color }}>
                          {philosophy.subtitle}
                        </p>
                      </div>
                    </div>

                    {/* Manifesto */}
                    <div className="flex-1 mb-6">
                      <h4 className="font-inter font-semibold text-foreground mb-4 flex items-center space-x-2">
                        <Icon name="FileText" size={16} className="text-text-secondary" />
                        <span>Manifesto</span>
                      </h4>
                      <motion.div
                        className="text-text-secondary leading-relaxed font-inter"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                      >
                        {philosophy.manifesto.split('\n\n').map((paragraph, idx) => (
                          <motion.p
                            key={idx}
                            className="mb-4 last:mb-0"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.3 + idx * 0.2 }}
                          >
                            {paragraph}
                          </motion.p>
                        ))}
                      </motion.div>
                    </div>

                    {/* Core Principles */}
                    <div>
                      <h4 className="font-inter font-semibold text-foreground mb-4 flex items-center space-x-2">
                        <Icon name="Compass" size={16} className="text-text-secondary" />
                        <span>Core Principles</span>
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {philosophy.principles.map((principle, idx) => (
                          <motion.div
                            key={idx}
                            className="flex items-center space-x-3 p-3 rounded-lg bg-surface/30 border border-border"
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.4, delay: 0.5 + idx * 0.1 }}
                          >
                            <div
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: philosophy.color }}
                            />
                            <span className="text-sm text-text-secondary font-inter">
                              {principle}
                            </span>
                          </motion.div>
                        ))}
                      </div>
                    </div>

                    {/* Progress Indicator */}
                    <div className="mt-6 pt-4 border-t border-border">
                      <div className="flex items-center justify-between text-xs text-text-tertiary font-mono">
                        <span>Philosophy {activePhilosophy + 1} of {philosophies.length}</span>
                        <div className="flex space-x-1">
                          {philosophies.map((_, idx) => (
                            <div
                              key={idx}
                              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                                idx === activePhilosophy 
                                  ? 'bg-brand-cyan' :'bg-surface'
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Abstract Background Elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-64 h-64 rounded-full opacity-5"
            style={{
              background: `radial-gradient(circle, ${philosophies[activePhilosophy]?.color || '#00d4ff'}40 0%, transparent 70%)`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`
            }}
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.05, 0.1, 0.05],
              rotate: [0, 180, 360]
            }}
            transition={{
              duration: 10 + i * 2,
              repeat: Infinity,
              ease: "linear"
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default DigitalPhilosophy;